import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Palette, Globe, Plus, MessageSquarePlus, MessageSquare, Settings as SettingsIcon, Sparkles } from "lucide-react";
import { fuzzyFilter, highlightMatches } from "../../lib/fuzzy";
import { NAV_GROUPS } from "../layout/Sidebar";
import { useThemeStore, type Theme } from "../../stores/theme";
import { searchVault, searchMessages, type SearchResult, type MessageSearchResult } from "../../lib/api";
import { wikiArticleRoute } from "../../lib/routes";
import i18n from "../../lib/i18n";
import css from "./CommandPalette.module.css";

export interface Command {
	id: string;
	title: string;
	/** Section heading shown above the row group. */
	group: string;
	/** Optional sort weight inside its group (lower first). */
	groupOrder?: number;
	/** Optional keyboard shortcut hint, e.g. "⌘K". */
	shortcut?: string;
	/** Optional emoji shortcut — rendered as the row icon when no iconNode is set. */
	icon?: string;
	/** Optional Lucide icon, emoji, or any ReactNode. */
	iconNode?: ReactNode;
	/** Extra terms used by the fuzzy matcher (synonyms, keywords). */
	keywords?: string;
	run: () => void;
}

const ARTICLE_DEBOUNCE_MS = 180;
const ARTICLE_LIMIT = 6;
const MESSAGE_LIMIT = 6;

/** Convert a vault-relative wiki path to a slug usable by /wiki/:slug. */
function pathToWikiSlug(path: string): string | null {
	if (!path.includes("/wiki/")) return null;
	const base = path.split("/").pop() ?? "";
	if (!base.endsWith(".md")) return null;
	return base.slice(0, -3);
}

/**
 * Global command palette mounted once at app root.
 *
 * Surfaces three layers of capability behind one Cmd/Ctrl-K shortcut:
 *
 *   1. **Pages** — every route in the sidebar's `NAV_GROUPS`. Single source
 *      of truth: adding a sidebar entry automatically makes it searchable.
 *   2. **Actions** — quick commands (new task, switch theme, change language,
 *      open settings, run wiki job). Reduce reliance on hunting through pages.
 *   3. **Articles** — live debounced full-text search across the vault via
 *      `GET /api/search`. Wiki articles open directly in the wiki viewer.
 *
 * Pages register a Cmd/Ctrl-K listener once, globally. Other pages may push
 * extra commands at runtime via the `agntspace:register-commands` event.
 */
export function CommandPalette() {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { setTheme, cycleTheme } = useThemeStore();

	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState("");
	const [activeIdx, setActiveIdx] = useState(0);
	const [extraCommands, setExtraCommands] = useState<Command[]>([]);
	const [articleResults, setArticleResults] = useState<SearchResult[]>([]);
	const [messageResults, setMessageResults] = useState<MessageSearchResult[]>([]);
	const [searching, setSearching] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);

	// --------------------------------------------------------------------
	// Page commands — built dynamically from the sidebar NAV_GROUPS so the
	// palette never drifts from the sidebar. Every nav item becomes a
	// command, with its real Lucide icon.
	// --------------------------------------------------------------------
	const pageCommands = useMemo<Command[]>(() => {
		const groupLabel = t("commandPalette.groupPages");
		const cmds: Command[] = [];
		for (const group of NAV_GROUPS) {
			const sectionName = group.labelKey ? t(group.labelKey) : "";
			for (const item of group.items) {
				const Icon = item.icon;
				cmds.push({
					id: `nav:${item.path}`,
					title: t(item.labelKey),
					group: groupLabel,
					iconNode: <Icon size={15} />,
					keywords: sectionName,
					run: () => navigate(item.path),
				});
			}
		}
		return cmds;
	}, [navigate, t]);

	// --------------------------------------------------------------------
	// Action commands — quick verbs that don't have their own page slot.
	// --------------------------------------------------------------------
	const actionCommands = useMemo<Command[]>(() => {
		const actionLabel = t("commandPalette.groupActions");
		const settingsLabel = t("commandPalette.groupSettings");
		const themeLabel = t("commandPalette.groupTheme");
		return [
			{
				id: "action:new-conversation",
				title: t("commandPalette.newConversation"),
				group: actionLabel,
				iconNode: <MessageSquarePlus size={15} />,
				run: () => navigate("/chat"),
			},
			{
				id: "action:new-task",
				title: t("tasks.newTask"),
				group: actionLabel,
				iconNode: <Plus size={15} />,
				run: () => {
					navigate("/tasks");
					setTimeout(() => window.dispatchEvent(new CustomEvent("agntspace:new-task")), 50);
				},
			},
			{
				id: "action:new-goal",
				title: t("goals.newGoal"),
				group: actionLabel,
				iconNode: <Plus size={15} />,
				run: () => {
					navigate("/goals");
					setTimeout(() => window.dispatchEvent(new CustomEvent("agntspace:new-goal")), 50);
				},
			},
			{
				id: "action:new-project",
				title: t("projects.newProject"),
				group: actionLabel,
				iconNode: <Plus size={15} />,
				run: () => {
					navigate("/projects");
					setTimeout(() => window.dispatchEvent(new CustomEvent("agntspace:new-project")), 50);
				},
			},
			{
				id: "action:run-wiki-job",
				title: t("commandPalette.runWikiJob"),
				group: actionLabel,
				iconNode: <Sparkles size={15} />,
				run: () => {
					navigate("/wiki");
					setTimeout(() => window.dispatchEvent(new CustomEvent("agntspace:run-wiki-job")), 50);
				},
			},
			{
				id: "action:open-settings",
				title: t("commandPalette.openSettings"),
				group: settingsLabel,
				iconNode: <SettingsIcon size={15} />,
				run: () => navigate("/settings"),
			},
			// Theme switchers
			{
				id: "theme:cycle",
				title: t("commandPalette.themeCycle"),
				group: themeLabel,
				iconNode: <Palette size={15} />,
				run: () => cycleTheme(),
			},
			...(["dark", "light", "zinc"] as Theme[]).map((mode) => ({
				id: `theme:${mode}`,
				title: t(`commandPalette.theme${mode.charAt(0).toUpperCase() + mode.slice(1)}`),
				group: themeLabel,
				iconNode: <Palette size={15} />,
				keywords: mode,
				run: () => setTheme(mode),
			})),
			// Language switchers
			{
				id: "lang:en",
				title: t("commandPalette.langEnglish"),
				group: settingsLabel,
				iconNode: <Globe size={15} />,
				keywords: "english language locale",
				run: () => i18n.changeLanguage("en"),
			},
			{
				id: "lang:sv",
				title: t("commandPalette.langSwedish"),
				group: settingsLabel,
				iconNode: <Globe size={15} />,
				keywords: "swedish svenska language locale",
				run: () => i18n.changeLanguage("sv"),
			},
			{
				id: "lang:de",
				title: t("commandPalette.langGerman"),
				group: settingsLabel,
				iconNode: <Globe size={15} />,
				keywords: "german deutsch language locale",
				run: () => i18n.changeLanguage("de"),
			},
		];
	}, [navigate, setTheme, cycleTheme, t]);

	const allCommands = useMemo(
		() => [...pageCommands, ...actionCommands, ...extraCommands],
		[pageCommands, actionCommands, extraCommands],
	);

	// --------------------------------------------------------------------
	// Global Cmd/Ctrl-K listener.
	// --------------------------------------------------------------------
	useEffect(() => {
		const handler = (e: KeyboardEvent) => {
			if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
				// A page with its own scoped palette claims Cmd+K by carrying
				// data-owns-command-palette on its root (the wiki does).
				// Yield — one keypress used to stack BOTH palettes.
				if (document.querySelector("[data-owns-command-palette]")) return;
				e.preventDefault();
				setOpen((v) => !v);
			} else if (e.key === "Escape" && open) {
				e.preventDefault();
				setOpen(false);
			}
		};
		document.addEventListener("keydown", handler);
		return () => document.removeEventListener("keydown", handler);
	}, [open]);

	useEffect(() => {
		const handler = (e: Event) => {
			const custom = e as CustomEvent<Command[]>;
			if (Array.isArray(custom.detail)) setExtraCommands(custom.detail);
		};
		window.addEventListener("agntspace:register-commands", handler);
		return () => window.removeEventListener("agntspace:register-commands", handler);
	}, []);

	useEffect(() => {
		if (open) {
			setQuery("");
			setActiveIdx(0);
			setArticleResults([]);
			setMessageResults([]);
			setTimeout(() => inputRef.current?.focus(), 0);
		}
	}, [open]);

	// --------------------------------------------------------------------
	// Live article search — debounced. Only fires when palette is open and
	// the query is at least 2 chars. Wiki paths only — non-wiki search hits
	// (raw notes, memory files) would not have a viewer to open them in.
	// --------------------------------------------------------------------
	useEffect(() => {
		if (!open) return;
		const q = query.trim();
		if (q.length < 2) {
			setArticleResults([]);
			setMessageResults([]);
			setSearching(false);
			return;
		}
		setSearching(true);
		const handle = setTimeout(async () => {
			// Articles + conversation messages in parallel; allSettled so one
			// failing source never blanks the other.
			const [arts, msgs] = await Promise.allSettled([
				searchVault(q, ARTICLE_LIMIT * 2),
				searchMessages(q, MESSAGE_LIMIT),
			]);
			if (arts.status === "fulfilled") {
				const wikiOnly = arts.value.filter((r) => pathToWikiSlug(r.path) !== null);
				setArticleResults(wikiOnly.slice(0, ARTICLE_LIMIT));
			} else {
				setArticleResults([]);
			}
			setMessageResults(msgs.status === "fulfilled" ? msgs.value : []);
			setSearching(false);
		}, ARTICLE_DEBOUNCE_MS);
		return () => clearTimeout(handle);
	}, [query, open]);

	// --------------------------------------------------------------------
	// Fuzzy filter commands.
	// --------------------------------------------------------------------
	const fuzzyResults = useMemo(
		() => fuzzyFilter(allCommands, query, (c) => `${c.group} ${c.title} ${c.keywords ?? ""}`),
		[allCommands, query],
	);
	const filteredCommands = useMemo(() => fuzzyResults.map((r) => r.item), [fuzzyResults]);
	const matchIndex = useMemo(() => {
		const map = new Map<string, number[]>();
		for (const r of fuzzyResults) map.set(r.item.id, r.matches);
		return map;
	}, [fuzzyResults]);

	// --------------------------------------------------------------------
	// Build the rendered row list. Inserts section headers between groups
	// and tracks which rows are selectable so keyboard nav skips headers.
	// --------------------------------------------------------------------
	type Row =
		| { kind: "header"; key: string; label: string }
		| { kind: "command"; key: string; cmd: Command }
		| { kind: "article"; key: string; article: SearchResult; slug: string }
		| { kind: "message"; key: string; message: MessageSearchResult };

	const rows = useMemo<Row[]>(() => {
		const out: Row[] = [];
		let lastGroup: string | null = null;
		for (const cmd of filteredCommands) {
			if (cmd.group !== lastGroup) {
				out.push({ kind: "header", key: `h:${cmd.group}`, label: cmd.group });
				lastGroup = cmd.group;
			}
			out.push({ kind: "command", key: cmd.id, cmd });
		}
		if (articleResults.length > 0) {
			out.push({
				kind: "header",
				key: "h:articles",
				label: t("commandPalette.groupArticles"),
			});
			for (const a of articleResults) {
				const slug = pathToWikiSlug(a.path);
				if (!slug) continue;
				out.push({ kind: "article", key: `a:${a.path}`, article: a, slug });
			}
		}
		if (messageResults.length > 0) {
			out.push({
				kind: "header",
				key: "h:messages",
				label: t("commandPalette.groupMessages"),
			});
			for (const m of messageResults) {
				out.push({ kind: "message", key: `m:${m.message_id}`, message: m });
			}
		}
		return out;
	}, [filteredCommands, articleResults, messageResults, t]);

	const selectableIndices = useMemo(
		() => rows.map((r, i) => (r.kind !== "header" ? i : -1)).filter((i) => i >= 0),
		[rows],
	);

	useEffect(() => {
		if (selectableIndices.length === 0) {
			setActiveIdx(0);
			return;
		}
		if (!selectableIndices.includes(activeIdx)) {
			setActiveIdx(selectableIndices[0] ?? 0);
		}
	}, [selectableIndices, activeIdx]);

	const moveActive = (delta: 1 | -1) => {
		if (selectableIndices.length === 0) return;
		const pos = selectableIndices.indexOf(activeIdx);
		const nextPos =
			pos === -1
				? 0
				: Math.min(selectableIndices.length - 1, Math.max(0, pos + delta));
		setActiveIdx(selectableIndices[nextPos] ?? 0);
	};

	const runRow = (idx: number) => {
		const row = rows[idx];
		if (!row || row.kind === "header") return;
		setOpen(false);
		if (row.kind === "command") {
			row.cmd.run();
		} else if (row.kind === "article") {
			navigate(wikiArticleRoute(row.slug));
		} else if (row.kind === "message") {
			navigate(
				`/chat/${row.message.conversation_id}?msg=${encodeURIComponent(row.message.message_id)}`,
			);
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "ArrowDown") {
			e.preventDefault();
			moveActive(1);
		} else if (e.key === "ArrowUp") {
			e.preventDefault();
			moveActive(-1);
		} else if (e.key === "Enter") {
			e.preventDefault();
			runRow(activeIdx);
		} else if (e.key === "Escape") {
			e.preventDefault();
			setOpen(false);
		}
	};

	if (!open) return null;

	const isEmpty = rows.length === 0;

	return (
		<div
			role="dialog"
			aria-modal="true"
			aria-label="Command palette"
			onClick={() => setOpen(false)}
			className={css.backdrop}
		>
			<div
				onClick={(e) => e.stopPropagation()}
				className={css.panel}
			>
				<div className={css.inputRow}>
					<input
						ref={inputRef}
						type="text"
						value={query}
						onChange={(e) => setQuery(e.target.value)}
						onKeyDown={handleKeyDown}
						placeholder={t("commandPalette.placeholder")}
						aria-label={t("commandPalette.placeholder")}
						className={css.input}
					/>
					{searching && (
						<span className={css.searchingHint}>
							{t("commandPalette.searching")}
						</span>
					)}
				</div>

				<div
					role="listbox"
					aria-label={t("commandPalette.placeholder")}
					className={css.listbox}
				>
					{isEmpty && (
						<div className={css.empty}>
							{t("commandPalette.noResults")}
						</div>
					)}
					{rows.map((row, idx) => {
						if (row.kind === "header") {
							return (
								<div key={row.key} className={css.sectionHeader}>
									{row.label}
								</div>
							);
						}
						const isActive = idx === activeIdx;
						if (row.kind === "command") {
							const cmd = row.cmd;
							const matches = matchIndex.get(cmd.id) ?? [];
							const titleOffset = cmd.group.length + 1;
							const titleMatches = matches
								.filter((m) => m >= titleOffset)
								.map((m) => m - titleOffset);
							const segments = highlightMatches(cmd.title, titleMatches);
							return (
								<div
									key={row.key}
									role="option"
									aria-selected={isActive}
									onMouseEnter={() => setActiveIdx(idx)}
									onClick={() => runRow(idx)}
									className={isActive ? css.rowActive : css.row}
								>
									{cmd.iconNode && (
										<span className={css.iconSlot}>
											{cmd.iconNode}
										</span>
									)}
									<span className={css.flexOne}>
										{segments.map((seg, i) =>
											seg.highlighted ? (
												<mark key={i}>{seg.text}</mark>
											) : (
												<span key={i}>{seg.text}</span>
											),
										)}
									</span>
								</div>
							);
						}
						if (row.kind === "message") {
							const m = row.message;
							return (
								<div
									key={row.key}
									role="option"
									aria-selected={isActive}
									onMouseEnter={() => setActiveIdx(idx)}
									onClick={() => runRow(idx)}
									className={isActive ? css.articleRowActive : css.articleRow}
								>
									<span className={css.iconSlotArticle}>
										<MessageSquare size={15} />
									</span>
									<span className={css.flexOneMin}>
										<div className={css.articleTitle}>{m.conversation_title}</div>
										{m.snippet && (
											<div className={css.articleSnippet}>{m.snippet}</div>
										)}
									</span>
								</div>
							);
						}
						// article row
						const art = row.article;
						return (
							<div
								key={row.key}
								role="option"
								aria-selected={isActive}
								onMouseEnter={() => setActiveIdx(idx)}
								onClick={() => runRow(idx)}
								className={isActive ? css.articleRowActive : css.articleRow}
							>
								<span className={css.iconSlotArticle}>
									<Sparkles size={15} />
								</span>
								<span className={css.flexOneMin}>
									<div className={css.articleTitle}>{art.title || row.slug}</div>
									{art.snippet && (
										<div className={css.articleSnippet}>
											{art.snippet}
										</div>
									)}
								</span>
							</div>
						);
					})}
				</div>

				<div className={css.footer}>
					{t("commandPalette.hint")}
				</div>
			</div>
		</div>
	);
}
